import React, { useState } from 'react';
import { X, AlertCircle, Calendar } from 'lucide-react';
import { publicationAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface PriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  publication: {
    id: number;
    title: string;
    priorityLevel?: 'normal' | 'priority';
    followUpDate?: string;
  };
  onPriorityUpdated: () => void;
}

const PriorityModal: React.FC<PriorityModalProps> = ({
  isOpen,
  onClose,
  publication,
  onPriorityUpdated
}) => {
  const [priorityLevel, setPriorityLevel] = useState<'normal' | 'priority'>(
    publication.priorityLevel || 'normal'
  );
  const [followUpDate, setFollowUpDate] = useState(
    publication.followUpDate || ''
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (priorityLevel === 'priority' && !followUpDate) {
      toast.error('Follow-up date is required for priority publications');
      return;
    }

    setIsLoading(true);
    try {
      await publicationAPI.updatePriority(publication.id, {
        priorityLevel,
        followUpDate: priorityLevel === 'priority' ? followUpDate : undefined
      });
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publication.id}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Publication priority updated successfully');
      onPriorityUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating priority:', error);
      toast.error('Failed to update publication priority');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Set Publication Priority
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Publication Title
            </h3>
            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
              {publication.title}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Priority Level
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="priority"
                    value="normal"
                    checked={priorityLevel === 'normal'}
                    onChange={(e) => setPriorityLevel(e.target.value as 'normal')}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    Normal Priority
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="priority"
                    value="priority"
                    checked={priorityLevel === 'priority'}
                    onChange={(e) => setPriorityLevel(e.target.value as 'priority')}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    High Priority
                  </span>
                </label>
              </div>
            </div>

            {priorityLevel === 'priority' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Follow-up Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>
                <div className="mt-2 flex items-start space-x-2">
                  <AlertCircle className="text-amber-500 mt-0.5" size={16} />
                  <p className="text-xs text-gray-600">
                    Set a follow-up date to track when this priority publication needs attention.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  priorityLevel === 'priority'
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? 'Updating...' : 'Update Priority'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PriorityModal;
